import React from 'react';
import { colorAt } from '../../../../ChartUtility/recharts/_kit';

/**
 * The filter controls of the comparison view.
 *
 * The compared dimension splits into two groups; every other dimension becomes
 * a single filter both groups share, badged COMMON so it is clear the reader
 * is holding it still rather than forgetting to set it twice.
 */

/** Group 1's colour, matching the first series the chart draws. */
export const G1_COLOR = colorAt(0);

/** Group 2's colour, matching the second. */
export const G2_COLOR = colorAt(1);

/** Marks a filter as held still across both groups. */
const CommonBadge = () => <span className="wc-common">COMMON</span>;

/**
 * A labelled control.
 * @param {string} label - What the control sets.
 * @param {string} htmlFor - Id of the control this labels.
 * @param {boolean} common - Whether both groups share it.
 * @param {React.ReactNode} children - The control.
 */
export const Field = ({ label, htmlFor, common = false, children }) => (
  <div className="wc-field">
    <label className="wc-label" htmlFor={htmlFor}>
      {label}
      {common && <CommonBadge />}
    </label>
    {children}
  </div>
);

/**
 * A single select, in the page's own styling rather than react-select's.
 * @param {string} id - Control id, for its label.
 * @param {string} value - Selected value.
 * @param {Function} onChange - Called with the new value.
 * @param {Array<{value: *, label: string}>} options - What can be picked.
 * @param {string} placeholder - Shown when nothing is picked.
 */
export const Picker = ({ id, value, onChange, options = [], placeholder }) => (
  <select
    id={id}
    className="wg-select"
    value={value ?? ''}
    onChange={(event) => onChange(event.target.value)}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

/**
 * The two group selectors for the compared dimension.
 *
 * Nothing is preselected, and each group's options exclude the other's pick,
 * so the two can never name the same thing — a comparison of a year against
 * itself is a bug the reader would have to notice from the numbers.
 *
 * @param {string} label - The dimension being split.
 * @param {string} id - Prefix for the two control ids.
 * @param {*} g1 - Group 1's value.
 * @param {*} g2 - Group 2's value.
 * @param {Function} onG1 - Called with Group 1's new value.
 * @param {Function} onG2 - Called with Group 2's new value.
 * @param {Array<{value: *, label: string}>} options - What can be picked.
 * @param {string} placeholder - Shown when nothing is picked.
 */
export const GroupPair = ({
  label,
  id,
  g1,
  g2,
  onG1,
  onG2,
  options = [],
  placeholder,
}) => {
  const without = (other) =>
    options.filter((option) => String(option.value) !== String(other));

  return (
    <div className="wc-pair">
      <span className="wc-label">{label}</span>
      <div className="wc-pair-row">
        <label className="wc-group" htmlFor={`${id}-g1`}>
          <span className="wc-group-name" style={{ color: G1_COLOR }}>Group 1</span>
          <Picker
            id={`${id}-g1`}
            value={g1}
            onChange={onG1}
            options={without(g2)}
            placeholder={placeholder}
          />
        </label>

        <span className="wc-vs">vs</span>

        <label className="wc-group" htmlFor={`${id}-g2`}>
          <span className="wc-group-name" style={{ color: G2_COLOR }}>Group 2</span>
          <Picker
            id={`${id}-g2`}
            value={g2}
            onChange={onG2}
            options={without(g1)}
            placeholder={placeholder}
          />
        </label>
      </div>
    </div>
  );
};

/**
 * The dimension the comparison splits on.
 * @param {Array<{key: string, label: string}>} dimensions - What can be compared.
 * @param {string} value - The dimension currently split on.
 * @param {Function} onChange - Called with the dimension to split on.
 */
export const CompareBy = ({ dimensions = [], value, onChange }) => (
  <div className="wc-compareby">
    <span className="wc-compareby-label">Compare by:</span>
    <div className="wc-toggle" role="tablist" aria-label="Compare by">
      {dimensions.map((dimension) => (
        <button
          key={dimension.key}
          type="button"
          role="tab"
          aria-selected={value === dimension.key}
          className={`wc-toggle-btn${value === dimension.key ? ' wc-toggle-btn--on' : ''}`}
          onClick={() => onChange(dimension.key)}
        >
          {dimension.label}
        </button>
      ))}
    </div>
  </div>
);
